import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import styled from '@emotion/styled';
import {
    color_defaults_white,
    color_shades_darkest,
    color_shades_darker,
    color_shades_dark,
    color_shades_light
} from '../../../../../shared/constants/colors';
import { MetricDefinition, getMetricDefinition } from '../../../../../shared/constants/analyticsConstants/metricsDefinitions';

interface MetricInfoTooltipProps {
    /** The metric key from METRICS_DEFINITIONS */
    metricKey: string;
    /** Optional: provide a custom definition instead of looking up by key */
    customDefinition?: MetricDefinition;
    /** Whether to show the calculation explanation */
    showCalculation?: boolean;
    /** Size of the info icon in pixels */
    iconSize?: number;
    /** Custom icon color */
    iconColor?: string;
    /** Additional CSS class */
    className?: string;
}

/**
 * Info icon SVG component
 */
const InfoIconSvg: React.FC<{ size: number; color?: string }> = ({ size, color = color_shades_dark }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <circle cx="8" cy="8" r="7" stroke={color} strokeWidth="1.5" fill="none" />
        <path
            d="M8 7V11M8 5.5V5"
            stroke={color}
            strokeWidth="1.5"
            strokeLinecap="round"
        />
    </svg>
);

/**
 * MetricInfoTooltip
 * 
 * Displays a small info icon that shows metric definition details on hover.
 * Use this component next to metric labels/titles to provide users with
 * context about how metrics are calculated.
 * 
 * @example
 * <TileTitle>
 *   Contained Conversations <MetricInfoTooltip metricKey="CONTAINED_CALLS_HEALTHCARE" />
 * </TileTitle>
 */
export const MetricInfoTooltip: React.FC<MetricInfoTooltipProps> = ({
    metricKey,
    customDefinition,
    showCalculation = false,
    iconSize = 14,
    iconColor,
    className
}) => {
    const [isHovered, setIsHovered] = useState(false);
    const [position, setPosition] = useState<{ x: number; y: number } | null>(null);

    const definition = customDefinition || getMetricDefinition(metricKey);

    if (!definition) {
        console.warn(`MetricInfoTooltip: No definition found for key "${metricKey}"`);
        return null;
    }

    const handleMouseEnter = (e: React.MouseEvent) => {
        setIsHovered(true);
        updatePosition(e);
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        updatePosition(e);
    };

    const handleMouseLeave = () => {
        setIsHovered(false);
        setPosition(null);
    };

    const updatePosition = (e: React.MouseEvent) => {
        const tooltipWidth = 320;
        const tooltipHeight = 250;
        const padding = 12;

        let x = e.clientX + padding;
        let y = e.clientY - 10;

        // Prevent tooltip from going off the right edge
        if (x + tooltipWidth > window.innerWidth) {
            x = e.clientX - tooltipWidth - padding;
        }

        // Prevent tooltip from going off the bottom edge
        if (y + tooltipHeight > window.innerHeight) {
            y = window.innerHeight - tooltipHeight - 10;
        }

        // Prevent tooltip from going off the top edge
        if (y < 10) {
            y = 10;
        }

        // Prevent tooltip from going off the left edge
        if (x < 10) {
            x = 10;
        }

        setPosition({ x, y });
    };

    const tooltipContent = isHovered && position && (
        <TooltipContainer
            style={{
                left: position.x,
                top: position.y
            }}
        >
            <TooltipTitle>{definition.name}</TooltipTitle>
            <TooltipDescription>{definition.description}</TooltipDescription>
            {showCalculation && (
                <>
                    <TooltipSectionTitle>How it's calculated:</TooltipSectionTitle>
                    <TooltipCalculation>{definition.calculation}</TooltipCalculation>
                </>
            )}
            {definition.notes && showCalculation && (
                <TooltipNote>
                    <strong>Note:</strong> {definition.notes}
                </TooltipNote>
            )}
        </TooltipContainer>
    );

    return (
        <>
            <IconWrapper
                className={className}
                onMouseEnter={handleMouseEnter}
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
                $iconSize={iconSize}
            >
                <InfoIconSvg size={iconSize} color={iconColor || color_shades_dark} />
            </IconWrapper>
            {tooltipContent && ReactDOM.createPortal(tooltipContent, document.body)}
        </>
    );
};

// Styled Components
const IconWrapper = styled.span<{ $iconSize: number }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: help;
    margin-left: 4px;
    vertical-align: middle;
    opacity: 0.5;
    transition: opacity 0.2s ease;

    &:hover {
        opacity: 1;
    }
`;

const TooltipContainer = styled.div`
    position: fixed;
    background: ${color_defaults_white};
    padding: 16px 18px;
    border-radius: 10px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    max-width: 320px;
    z-index: 99999;
    pointer-events: none;
    border: 1px solid ${color_shades_light};
`;

const TooltipTitle = styled.div`
    font-weight: 700;
    font-size: 14px;
    color: ${color_shades_darkest};
    margin-bottom: 8px;
    letter-spacing: -0.2px;
`;

const TooltipDescription = styled.div`
    font-size: 13px;
    color: ${color_shades_darker};
    line-height: 1.5;
    margin-bottom: 12px;
`;

const TooltipSectionTitle = styled.div`
    font-size: 11px;
    font-weight: 600;
    color: ${color_shades_dark};
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 6px;
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid ${color_shades_light};
`;

const TooltipCalculation = styled.div`
    font-size: 12px;
    color: ${color_shades_darker};
    line-height: 1.5;
`;

const TooltipNote = styled.div`
    font-size: 11px;
    color: ${color_shades_dark};
    line-height: 1.4;
    margin-top: 12px;
    padding: 10px;
    background: rgba(0, 0, 0, 0.03);
    border-radius: 6px;
    
    strong {
        color: ${color_shades_darker};
    }
`;

export default MetricInfoTooltip;
